

import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import Badge from "../components/Badge";
import Modal from "../components/Modal";
import InvoiceForm from "./InvoiceForm";
import { invoicesApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useSettings } from "@/context/SettingsContext";
import { formatCurrency, formatCurrencyFull } from "@/lib/data";
import { sendInvoiceEmail } from "@/lib/emailService";
import {
  Plus,
  Search,
  FileText,
  Download,
  Mail,
  CheckCircle2,
  Clock,
  AlertCircle,
  Eye,
  Edit3,
  Trash2,
  CreditCard,
  Printer,
} from "lucide-react";
import PrintableInvoice from "./components/PrintableInvoice";

const statuses = ["all", "paid", "partial", "pending"] as const;
type Status = typeof statuses[number];

interface Invoice {
  id: number;
  invoice_number: string;
  event_id: number;
  client_id: number;
  client?: { id: number; name: string; phone?: string; email?: string };
  event?: { 
    id: number; 
    name: string; 
    event_type?: string; 
    event_date?: string; 
    venue?: string;
    event_vendors?: EventVendor[];
  };
  total_amount: number;
  discount_amount: number;
  tax_amount: number;
  final_amount: number;
  paid_amount: number;
  balance_amount: number;
  status: string;
  due_date: string;
  invoice_date: string;
  notes: string;
  created_at?: string;
}

interface EventVendor {
  id: number;
  vendor_id: number;
  vendor?: {
    id: number;
    name: string;
    phone?: string;
    email?: string;
  };
  service_name: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  notes?: string;
}

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { settings } = useSettings();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<Status>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  
  // Email modal state
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailingInvoice, setEmailingInvoice] = useState<Invoice | null>(null);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailStatus, setEmailStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [emailError, setEmailError] = useState("");

  // Print invoice state
  const [printInvoice, setPrintInvoice] = useState<Invoice | null>(null);

  // Fetch invoices from API
  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const response = await invoicesApi.list();
      const invoicesData = response.data?.data || response.data || [];
      setInvoices(invoicesData);
    } catch (error) {
      showToast("Failed to load invoices", "error");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let result = activeFilter === "all" ? invoices : invoices.filter(i => i.status === activeFilter);
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(inv => 
        (inv.invoice_number || String(inv.id)).toLowerCase().includes(query) ||
        (inv.client?.name || "").toLowerCase().includes(query) ||
        (inv.event?.name || "").toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [activeFilter, invoices, searchQuery]);

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this invoice?")) return;
    try {
      await invoicesApi.delete(id);
      showToast("Invoice deleted successfully", "success");
      loadInvoices();
    } catch (error) {
      showToast("Failed to delete invoice", "error");
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      if (editingInvoice) {
        await invoicesApi.update(editingInvoice.id, data);
        showToast("Invoice updated successfully", "success");
      } else {
        await invoicesApi.create(data);
        showToast("Invoice created successfully", "success");
      }
      setIsModalOpen(false);
      setEditingInvoice(null);
      loadInvoices();
    } catch (error) {
      showToast("Failed to save invoice", "error");
    }
  };

  // Print invoice using browser's print functionality
  const handlePrintInvoice: (inv: Invoice) => void = (inv) => {
    setPrintInvoice(inv);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintInvoice(null), 100);
    }, 100);
  };

  // Hook for after-print notification
  useEffect(() => {
    const handleAfterPrint = () => {
      if (printInvoice) {
        showToast("Invoice ready - use browser's save as PDF option", "success");
      }
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, [printInvoice, showToast]);

  const sendEmail = async () => {
    if (!emailRecipient || !emailingInvoice) return;
    
    setEmailStatus("sending");
    try {
      const result = await sendInvoiceEmail(emailRecipient, {
        id: emailingInvoice.invoice_number || String(emailingInvoice.id),
        client: emailingInvoice.client?.name || "Unknown",
        event: emailingInvoice.event?.name || "-",
        total: emailingInvoice.total_amount,
        final: emailingInvoice.final_amount,
        balance: emailingInvoice.balance_amount,
        dueDate: emailingInvoice.due_date,
      });
      if (result.success) {
        setEmailStatus("success");
        showToast("Email sent successfully", "success");
      } else {
        setEmailStatus("error");
        setEmailError(result.error || "Failed to send email");
      }
    } catch (error) {
      setEmailStatus("error");
      showToast("Failed to send email", "error");
    }
  };

  const statusConfig = {
    paid: { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
    partial: { color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
    pending: { color: "bg-rose-50 text-rose-700 border-rose-200", icon: AlertCircle },
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold text-stone-800">Invoices</h1>
          <p className="text-xs text-stone-500">{filtered.length} invoices • {formatCurrencyFull(invoices.reduce((s, i) => s + (i.final_amount || 0), 0))} total</p>
        </div>
        <button 
          onClick={() => { setEditingInvoice(null); setIsModalOpen(true); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all"
        >
          <Plus className="w-4 h-4" />
          Generate
        </button>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            placeholder="Search invoices, clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-stone-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm"
          />
        </div>

        {/* Status Filters */}
        <div className="flex gap-1.5 overflow-x-auto">
          {statuses.map((s) => {
            const count = s === "all" ? invoices.length : invoices.filter(i => i.status === s).length;
            const isActive = activeFilter === s;
            return (
              <button
                key={s}
                onClick={() => setActiveFilter(s)}
                className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  isActive
                    ? "bg-stone-800 text-white"
                    : "bg-white text-stone-600 border border-stone-200 hover:bg-stone-50"
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
                <span className={`ml-1.5 px-1 py-0.5 rounded text-[10px] ${isActive ? "bg-white/20" : "bg-stone-100"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Compact Table - Payment Details Focus */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200">
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase">Invoice</th>
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase">Client</th>
                <th className="text-right px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase">Total</th>
                <th className="text-right px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase">Paid</th>
                <th className="text-right px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase">Balance</th>
                <th className="text-center px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase">Status</th>
                <th className="text-right px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {filtered.map((inv) => {
                const StatusIcon = statusConfig[inv.status as keyof typeof statusConfig]?.icon || Clock;
                const isOverdue = new Date(inv.due_date) < new Date() && inv.status !== "paid";
                
                return (
                  <tr key={inv.id} className="hover:bg-stone-50/50 transition-colors">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <div className="font-medium text-stone-800 text-sm">{inv.invoice_number || `INV-${String(inv.id).padStart(4, "0")}`}</div>
                          <div className="text-xs text-stone-400">{inv.invoice_date}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-stone-700 text-sm truncate max-w-[120px]">{inv.client?.name || "—"}</div>
                      <div className="text-xs text-stone-400 truncate max-w-[120px]">{inv.event?.name || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-medium text-stone-800">{formatCurrency(inv.final_amount)}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-medium text-emerald-600">{formatCurrency(inv.paid_amount || 0)}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {inv.balance_amount > 0 ? (
                        <div className="font-medium text-rose-600">{formatCurrency(inv.balance_amount)}</div>
                      ) : (
                        <div className="font-medium text-emerald-600 text-xs">✓ Paid</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${statusConfig[inv.status as keyof typeof statusConfig]?.color || "bg-stone-100 text-stone-600"}`}>
                        <StatusIcon className="w-3 h-3" />
                        <span className="capitalize">{inv.status}</span>
                      </div>
                      {isOverdue && <div className="text-[10px] text-rose-600 mt-0.5">Overdue</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-0.5">
                        <button 
                          onClick={() => handlePrintInvoice(inv)}
                          className="p-1.5 rounded text-stone-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => { setEmailingInvoice(inv); setIsEmailModalOpen(true); }}
                          className="p-1.5 rounded text-stone-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Email"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => { setEditingInvoice(inv); setIsModalOpen(true); }}
                          className="p-1.5 rounded text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
                          title="Edit"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(inv.id)}
                          className="p-1.5 rounded text-stone-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-stone-100 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-stone-300" />
                    </div>
                    <h3 className="text-sm font-semibold text-stone-700">No invoices found</h3>
                    <p className="text-xs text-stone-400 mt-1">Try adjusting filters or create a new invoice</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Email Modal */}
      <Modal
        isOpen={isEmailModalOpen}
        onClose={() => { setIsEmailModalOpen(false); setEmailingInvoice(null); setEmailRecipient(""); setEmailStatus("idle"); setEmailError(""); }}
        title="Send Invoice via Email"
        size="md"
      >
        {emailingInvoice && (
          <div className="space-y-4">
            <div className="bg-stone-50 p-3 rounded-lg">
              <div className="text-sm font-semibold text-stone-800">{emailingInvoice.invoice_number || emailingInvoice.id}</div>
              <div className="text-xs text-stone-500">{emailingInvoice.client?.name || "Unknown"}</div>
              <div className="text-sm font-bold text-amber-600 mt-1">{formatCurrencyFull(emailingInvoice.final_amount || 0)}</div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Recipient Email</label>
              <input
                type="email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder="customer@example.com"
                className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm"
              />
            </div>

            {emailStatus === "success" && (
              <div className="p-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Email sent successfully!
              </div>
            )}

            {emailStatus === "error" && (
              <div className="p-2 bg-rose-50 text-rose-700 rounded-lg text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {emailError || "Failed to send email"}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setIsEmailModalOpen(false)}
                className="flex-1 px-3 py-2 rounded-lg border border-stone-200 text-stone-600 font-medium text-sm hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                onClick={sendEmail}
                disabled={emailStatus === "sending" || !emailRecipient}
                className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 text-white font-medium text-sm hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {emailStatus === "sending" ? (
                  <>
                    <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                    Sending...
                  </>
                ) : "Send Email"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add/Edit Invoice Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingInvoice(null); }}
        title={editingInvoice ? "Edit Invoice" : "Generate New Invoice"}
        size="lg"
      >
        <InvoiceForm
          initialData={editingInvoice ? {
            client: editingInvoice.client?.name || "",
            event: editingInvoice.event?.name || "",
            total: String(editingInvoice.total_amount || 0),
            discount: String(editingInvoice.discount_amount || 0),
            advance: String(editingInvoice.paid_amount || 0),
            status: editingInvoice.status,
            date: editingInvoice.invoice_date,
            dueDate: editingInvoice.due_date,
          } : undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => { setIsModalOpen(false); setEditingInvoice(null); }}
        />
      </Modal>

      {printInvoice && (
        <PrintableInvoice
          invoice={printInvoice}
          businessInfo={{
            name: settings?.business?.businessName || 'Shalimar',
            logoUrl: settings?.logoUrl,
            tagline: settings?.business?.tagline,
            address: settings?.business?.address,
            phone: settings?.business?.phone,
            email: settings?.business?.email,
            gstNumber: settings?.business?.gstNumber,
          }}
        />
      )}
    </DashboardLayout>
  );
}
